/**
 * Every database path to clear when a user is deleted, as one multi-location
 * update.
 *
 * This used to be a string of fire-and-forget writes, each one rewriting a
 * whole map from the browser's copy. Any that failed after the Auth account
 * was gone were never retried, and nothing touched other users' admins maps
 * or consent, so deleted uids stayed on as hub members, folder managers and
 * admins. Hub saves then wrote users/$deleted/admins/$manager and grew the
 * /users record back. One atomic update either removes all of it or none.
 *
 * Each entry is removed by its own path rather than by rewriting the map, so
 * a concurrent edit to the same hub or group is not overwritten.
 *
 * Everything that may be written here is open to any signed-in user except
 * users/$uid and userprofiles/$uid, which need the same rights the delete
 * already needed, so folding them together cannot refuse a delete that used
 * to succeed. /systemManagers is console-only and is handled separately.
 *
 * Takes the DatabaseContext shapes:
 *   folder.users, folder.managers, file.managers, user.admins  key arrays
 *   group.users                                                 [{ key }]
 * and the RAW /notifications value, because the context only holds the
 * current user's own notifications.
 */
export function userDeletionUpdates(
    uid,
    { users = [], folders = [], groups = [], files = [], notifications = {} },
) {
    let updates = {};

    folders.forEach((folder) => {
        if ((folder.users || []).includes(uid)) {
            updates[`folders/${folder.key}/users/${uid}`] = null;
        }
        if ((folder.managers || []).includes(uid)) {
            updates[`folders/${folder.key}/managers/${uid}`] = null;
        }
    });

    groups.forEach((group) => {
        if ((group.users || []).some((member) => member.key === uid)) {
            updates[`groups/${group.key}/users/${uid}`] = null;
        }
    });

    files.forEach((file) => {
        if ((file.managers || []).includes(uid)) {
            updates[`files/${file.key}/managers/${uid}`] = null;
        }
    });

    // The people this user administered. Their own admins map goes with
    // users/$uid below, and naming both would be refused as overlapping.
    users.forEach((other) => {
        if (other.key !== uid && (other.admins || []).includes(uid)) {
            updates[`users/${other.key}/admins/${uid}`] = null;
        }
    });

    Object.entries(notifications || {}).forEach(([key, notification]) => {
        ["users", "managers", "seenBy"].forEach((field) => {
            if (
                notification &&
                notification[field] &&
                notification[field][uid]
            ) {
                updates[`notifications/${key}/${field}/${uid}`] = null;
            }
        });
    });

    updates[`userconsent/${uid}`] = null;
    updates[`userlogin/${uid}`] = null;
    updates[`users/${uid}`] = null;
    // Leaving an orphaned profile would keep a deleted person's personal data
    updates[`userprofiles/${uid}`] = null;

    return updates;
}
